import React, { useState, useCallback, useMemo } from "react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { Server, TrendingUp, Wifi, RefreshCw } from "lucide-react";
import {
  OverviewStats,
  PerformanceCharts,
  NetworkTraffic,
  LogsModalWithData,
  InfrastructureHeader,
  ServerOverviewGrid,
} from "@/shared/components/admin/AdminServers";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Hooks
import useInfrastructureSystemData from "@/shared/hooks/useInfrastructureSystemData";
import useInfrastructureSnapshots from "@/shared/hooks/useInfrastructureSnapshots";
import useInfrastructurePerformance from "@/shared/hooks/useInfrastructurePerformance";

export default function AdminServers() {
  const { t } = useLanguage();

  // 1. UI State
  const [timeRange, setTimeRange] = useState("24h");
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedServerForLogs, setSelectedServerForLogs] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // 2. Modular Logic Hooks
  const {
    systemStats,
    bankserverHealth,
    displayServers,
    refetchers: systemRefetchers,
  } = useInfrastructureSystemData();
  const resourceSnapshots = useInfrastructureSnapshots(
    systemStats,
    bankserverHealth,
  );
  const { performanceData, refetchers: perfRefetchers } =
    useInfrastructurePerformance(timeRange, resourceSnapshots);

  // 3. Config & Translations
  const statusConfig = useMemo(
    () => ({
      healthy: {
        icon: RefreshCw,
        color: "text-emerald-500",
        bg: "bg-emerald-100",
        label: t("healthy"),
      },
      warning: {
        icon: RefreshCw,
        color: "text-amber-500",
        bg: "bg-amber-100",
        label: t("warning"),
      },
      critical: {
        icon: RefreshCw,
        color: "text-red-500",
        bg: "bg-red-100",
        label: t("critical"),
      },
      offline: {
        icon: RefreshCw,
        color: "text-slate-500",
        bg: "bg-slate-100",
        label: t("offline"),
      },
      disabled: {
        icon: RefreshCw,
        color: "text-slate-400",
        bg: "bg-slate-100",
        label: t("disabled"),
      },
    }),
    [t],
  );

  const translations = useMemo(
    () => ({
      serverNames: {
        "API Gateway": t("apiGateway"),
        "Payment Processor": t("paymentProcessor"),
        Bankserver: t("bankserver"),
        "Database Primary": t("databasePrimary"),
        "Database Replica": t("databaseReplica"),
        "Webhook Handler": t("webhookHandler"),
        "Queue Processor": t("queueProcessor"),
        "Load Balancer": t("loadBalancer"),
        "Redis Cache": t("redisCache"),
      },
      regions: { Singapore: t("singapore"), Bangkok: t("bangkok") },
      logStatus: {
        UP: t("statusUp"),
        LOW: t("statusLow"),
        CRITICAL: t("statusCritical"),
        DOWN: t("statusDown"),
      },
    }),
    [t],
  );

  // 4. Handlers
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        ...Object.values(systemRefetchers).map((refetch) => refetch()),
        ...Object.values(perfRefetchers).map((refetch) => refetch()),
      ]);
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  }, [systemRefetchers, perfRefetchers]);

  const handleViewLogs = useCallback((server, e) => {
    e.stopPropagation();
    setSelectedServerForLogs(server);
    setLogsDialogOpen(true);
  }, []);

  return (
    <div className="space-y-6">
      <InfrastructureHeader
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        handleRefresh={handleRefresh}
        refreshing={refreshing}
      />

      {/* Overview Stats */}
      {systemStats ? (
        <OverviewStats servers={displayServers} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white p-4 rounded-xl shadow-sm border border-slate-100"
            >
              <Skeleton width={100} height={14} />
              <Skeleton width={150} height={24} className="mt-2" />
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-slate-100 w-full overflow-x-auto justify-start whitespace-nowrap">
          <TabsTrigger value="overview" className="gap-2 shrink-0">
            <Server className="h-4 w-4" />
            {t("overview")}
          </TabsTrigger>
          <TabsTrigger value="performance" className="gap-2 shrink-0">
            <TrendingUp className="h-4 w-4" />
            {t("performance")}
          </TabsTrigger>
          <TabsTrigger value="network" className="gap-2 shrink-0">
            <Wifi className="h-4 w-4" />
            {t("network")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <ServerOverviewGrid
            isLoading={!systemStats}
            displayServers={displayServers}
            statusConfig={statusConfig}
            handleViewLogs={handleViewLogs}
            translations={translations}
          />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <PerformanceCharts
            historicalData={performanceData}
            timeRange={timeRange}
          />
        </TabsContent>

        {/* Network Tab */}
        <TabsContent value="network">
          <NetworkTraffic
            servers={displayServers}
            translateServerName={(name) =>
              translations.serverNames[name] || name
            }
          />
        </TabsContent>
      </Tabs>

      {/* Server Logs Dialog - Lazy load logs on demand */}
      {logsDialogOpen && (
        <LogsModalWithData
          selectedServer={selectedServerForLogs}
          onClose={() => setLogsDialogOpen(false)}
          translateLogStatus={(status) =>
            translations.logStatus[status] || status
          }
        />
      )}
    </div>
  );
}
